import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import BrandMark from "../components/BrandMark";
import PasswordInput from "../components/PasswordInput";

const rules = [
  { text: "Minimum 12 characters", check: (p) => p.length >= 12 },
  { text: "At least 1 uppercase letter", check: (p) => /[A-Z]/.test(p) },
  { text: "At least 1 lowercase letter", check: (p) => /[a-z]/.test(p) },
  { text: "At least 1 number", check: (p) => /\d/.test(p) },
  { text: "At least 1 special character", check: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function RotatePassword() {
  const { user, load } = useAuth();
  const navigate = useNavigate();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [visibilityResetKey, setVisibilityResetKey] = useState(0);

  if (user === false) return <Navigate to="/login" replace />;
  if (user && !user.must_rotate_password) return <Navigate to="/owner" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== pw2) return setErr("Passwords do not match");
    if (!rules.every((r) => r.check(pw))) return setErr("Password does not meet requirements");
    if (pw === cur) return setErr("New password must differ from current password");
    setBusy(true);
    try {
      const r = await api.post("/auth/rotate-password", { current_password: cur, new_password: pw });
      if (r?.data?.access_token) {
        const { setAccessToken } = await import("../lib/api");
        setAccessToken(r.data.access_token);
      }
      await load();
      navigate("/owner");
    } catch (e) {
      if (e?.response?.status === 401) {
        setErr("Your session expired. Please sign in again.");
        setTimeout(() => navigate("/login"), 1500);
      } else {
        setErr(formatApiError(e.response?.data?.detail) || e.message);
      }
    } finally {
      setBusy(false);
      setVisibilityResetKey((k) => k + 1);
    }
  };

  return (
    <div className="min-h-screen bg-erf-dark/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="rotate-password-page">
      <div className="fixed top-0 left-0 right-0 bg-red-600 text-white font-nav text-xs uppercase tracking-widest px-4 py-2 text-center z-50" data-testid="rotate-required-banner">
        Password rotation required · You cannot use ERF ConstructionOS until you rotate your password
      </div>
      <div className="w-full max-w-md bg-erf-cream border-t-4 border-t-erf-gold border border-erf-borderCream rounded-lg shadow-2xl p-6 space-y-5">
        <div className="flex items-center gap-3">
          <BrandMark size={56} />
          <div>
            <div className="font-display font-bold text-lg text-erf-dark">Security Requirement</div>
            <div className="text-xs font-nav text-erf-dark/60">Forced password rotation — first login</div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="rotate-current" className="text-xs uppercase font-nav tracking-wider text-erf-dark/70">Current temporary password</Label>
            <PasswordInput id="rotate-current" value={cur} onChange={(e) => setCur(e.target.value)} required className="font-mono mt-1" testId="rotate-current-input" resetVisibilityKey={visibilityResetKey} autoComplete="current-password" />
          </div>
          <div>
            <Label htmlFor="rotate-new" className="text-xs uppercase font-nav tracking-wider text-erf-dark/70">New executive password</Label>
            <PasswordInput id="rotate-new" value={pw} onChange={(e) => setPw(e.target.value)} required className="font-mono mt-1" testId="rotate-new-input" resetVisibilityKey={visibilityResetKey} autoComplete="new-password" />
          </div>
          <div>
            <Label htmlFor="rotate-confirm" className="text-xs uppercase font-nav tracking-wider text-erf-dark/70">Confirm new password</Label>
            <PasswordInput id="rotate-confirm" value={pw2} onChange={(e) => setPw2(e.target.value)} required className="font-mono mt-1" testId="rotate-confirm-input" resetVisibilityKey={visibilityResetKey} autoComplete="new-password" />
          </div>

          <ul className="text-xs space-y-1 font-nav" data-testid="rotate-rules">
            {rules.map((r) => {
              const ok = r.check(pw);
              return (
                <li key={r.text} className={ok ? "text-green-700" : "text-erf-dark/60"}>
                  {ok ? "✓" : "•"} {r.text}
                </li>
              );
            })}
          </ul>

          {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2" data-testid="rotate-error">{err}</div>}

          <Button
            type="submit"
            disabled={busy}
            className="w-full h-10 bg-erf-gold hover:bg-erf-goldHover text-erf-dark font-nav uppercase tracking-widest rounded-sm"
            data-testid="rotate-submit-btn"
          >
            {busy ? "Rotating…" : "Rotate password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
